// src/components/profile/DependentFormModal.tsx
import React, { JSX } from 'react';
import { Save, X } from 'lucide-react';
import { DependentRecord } from '../../pages/Profile';
import { LookupValue } from '../../services/lookupService';

// Import react-datepicker
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface DependentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  form: DependentRecord;
  setForm: React.Dispatch<React.SetStateAction<DependentRecord>>;
  onSubmit: () => void;
  isSubmitting: boolean;
  editingId: string | null;
  countries: LookupValue[];
  states: LookupValue[];
  cities: LookupValue[];
  currencies: LookupValue[];
  getOptions: (group: string) => LookupValue[];
  renderDropdownOptions: (options: LookupValue[], placeholder?: string) => JSX.Element;
}

const DependentFormModal: React.FC<DependentFormModalProps> = (props) => {
  const {
    isOpen, onClose, form, setForm, onSubmit, isSubmitting, editingId,
    countries, states, cities, currencies, getOptions, renderDropdownOptions
  } = props;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-4 sm:p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-[12px] sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-4">
          {editingId ? 'Edit Dependent' : 'Add Dependent'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-4">
          {/* Name */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
              required
            />
          </div>

          {/* Relation */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">Relation *</label>
            <select
              value={form.relation}
              onChange={e => setForm({ ...form, relation: e.target.value })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
              required
            >
              {renderDropdownOptions(getOptions('relation'), 'select')}
            </select>
          </div>

          {/* Profile Type */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">Profile Type</label>
            <select
              value={form.profileType}
              onChange={e => setForm({ ...form, profileType: e.target.value })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
            >
              {renderDropdownOptions(getOptions('profileType').filter(opt => opt.value !== '1'), 'select')}
            </select>
          </div>

          {/* Gender */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">Gender</label>
            <select
              value={form.gender}
              onChange={e => setForm({ ...form, gender: e.target.value })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
            >
              {renderDropdownOptions(getOptions('gender'), 'select')}
            </select>
          </div>

          {/* Date of Birth - Now with Calendar */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
            <DatePicker
              selected={form.dob ? new Date(form.dob) : null}
              onChange={(date: Date | null) => {
                setForm({
                  ...form,
                  dob: date ? date.toISOString().split('T')[0] : ''
                });
              }}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select date"
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={50}
              maxDate={new Date()}
            />
          </div>

          {/* Marital Status */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">Marital Status</label>
            <select
              value={form.maritalStatus}
              onChange={e => setForm({ ...form, maritalStatus: e.target.value })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
            >
              {renderDropdownOptions(getOptions('maritalStatus'), 'select')}
            </select>
          </div>

          {/* Country */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">Country *</label>
            <select
              value={form.country}
              onChange={e => setForm({ ...form, country: e.target.value, state: '', city: '', currency: '' })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
              required
            >
              {renderDropdownOptions(countries, 'Select Country')}
            </select>
          </div>

          {/* State */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">State *</label>
            <select
              value={form.state}
              onChange={e => setForm({ ...form, state: e.target.value, city: '' })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
              required
            >
              {renderDropdownOptions(states, 'Select State')}
            </select>
          </div>

          {/* City */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">City *</label>
            <select
              value={form.city}
              onChange={e => setForm({ ...form, city: e.target.value })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
              required
            >
              {renderDropdownOptions(cities, 'Select City')}
            </select>
          </div>

          {/* Currency */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">Currency *</label>
            <select
              value={form.currency}
              onChange={e => setForm({ ...form, currency: e.target.value })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
              required
            >
              {renderDropdownOptions(currencies, 'Select Currency')}
            </select>
          </div>

          {/* Employment Status */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">Employment Status</label>
            <select
              value={form.employmentStatus}
              onChange={e => setForm({ ...form, employmentStatus: e.target.value })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
            >
              {renderDropdownOptions(getOptions('employmentStatus'), 'select')}
            </select>
          </div>

          {/* Planned Retirement Age */}
          <div>
            <label className="block text-[11px] sm:text-sm font-medium text-gray-700 mb-1">Planned Retirement Age</label>
            <input
              type="number"
              value={form.retirementAge}
              onChange={e => setForm({ ...form, retirementAge: parseInt(e.target.value) || 0 })}
              className="w-full p-1 sm:p-2 border border-gray-300 rounded-lg text-[11px] sm:text-base"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-2 sm:space-x-4 mt-4 sm:mt-6">
          <button
            onClick={onClose}
            className="bg-gray-200 text-gray-700 py-1 sm:py-2 px-2 sm:px-4 rounded-lg hover:bg-gray-300 flex items-center"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="text-[11px] sm:text-base">Cancel</span>
          </button>
          <button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="bg-blue-600 text-white py-1 sm:py-2 px-2 sm:px-4 rounded-lg hover:bg-blue-700 flex items-center disabled:opacity-50"
          >
            <Save className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="text-[11px] sm:text-base">
              {editingId ? 'Update Dependent' : 'Add Dependent'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DependentFormModal;